import { useEffect, useRef } from "react";
import { BONE, createFlock, paintFlock, stepFlock } from "../lib/butterflyFlight.js";

const CELL_W = 8; // CSS px per character cell
const CELL_H = 12;
const FONT_PX = 11;
// Focal length as a share of the panel's width: close enough that each
// butterfly spans enough characters to read as one.
const FOCAL = 1.4;
const FPS = 30; // a terminal's cadence: characters change calmly, not every frame
// Sparse to dense, so brighter cells get heavier characters.
const RAMP = " .,:;-~=+tox%X$8@";
const GRID_ALPHA = 0.08; // the faint "." in every cell behind the flock

// The sign-in page's swallowtails as ASCII. Each frame draws them smoothly
// into a tiny canvas with one pixel per character cell, so its antialiasing
// gives each cell's coverage, and then sets a character of matching density.
export default function ButterflyField({ className = "" }) {
    const ref = useRef(null);

    useEffect(() => {
        const cvs = ref.current;
        const host = cvs?.parentElement;
        const ctx = cvs?.getContext("2d");
        const sample = document.createElement("canvas");
        const sg = sample.getContext("2d", { willReadFrequently: true });
        if (!host || !ctx || !sg) return;
        const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const font = getComputedStyle(document.documentElement).getPropertyValue("--mono").trim() || "monospace";

        let view = null, flock = null, glyphs = null, grid = null;
        let cols = 0, rows = 0, dpr = 1;
        let frame = 0, last = 0, pending = 0, stopped = false;
        // x, y in [-1, 1] across the panel, y up. A rest anchor ignores jitter.
        const pointer = { x: 0, y: 0, present: false, speed: 0, restS: 0, movedAt: 0, ax: 0, ay: 0 };

        function render(now, dt) {
            if (!view) return;
            const t = now / 1000;
            if (dt > 0) {
                pointer.restS = pointer.present ? (now - pointer.movedAt) / 1000 : 0;
                stepFlock(flock, dt, t, view, pointer);
                pointer.speed *= Math.max(0, 1 - dt * 6);
            }
            sg.setTransform(1 / CELL_W, 0, 0, 1 / CELL_H, 0, 0);
            paintFlock(sg, flock, view, t);
            const cells = sg.getImageData(0, 0, cols, rows).data;

            ctx.clearRect(0, 0, cvs.width, cvs.height);
            ctx.drawImage(grid, 0, 0);
            const gw = CELL_W * dpr, gh = CELL_H * dpr;
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const i = (r * cols + c) * 4;
                    // Brightness times coverage: the scene is bone on transparent.
                    const v = (cells[i] / BONE[0]) * (cells[i + 3] / 255);
                    const level = Math.min(RAMP.length - 1, Math.floor(Math.pow(v, 0.85) * RAMP.length));
                    if (level > 0) ctx.drawImage(glyphs, level * gw, 0, gw, gh, c * gw, r * gh, gw, gh);
                }
            }
        }

        function resize() {
            dpr = window.devicePixelRatio || 1;
            const w = host.clientWidth, h = host.clientHeight;
            cols = Math.floor(w / CELL_W);
            rows = Math.floor(h / CELL_H);
            if (!cols || !rows) { view = null; return; }
            cvs.width = Math.round(w * dpr);
            cvs.height = Math.round(h * dpr);
            cvs.style.width = `${w}px`;
            cvs.style.height = `${h}px`;
            sample.width = cols;
            sample.height = rows;
            const f = w * FOCAL;
            view = { W: w, H: h, f, hwu: w / 2 / f, hhu: h / 2 / f };
            glyphs = glyphAtlas(font, dpr);
            grid = cellGrid(font, dpr, cvs.width, cvs.height, cols, rows);
            flock ??= createFlock(view, { still });
            render(performance.now(), 0);
        }

        function tick(now) {
            if (stopped || document.hidden) return;
            pending += Math.min(0.1, (now - (last || now)) / 1000);
            last = now;
            if (pending >= 1 / FPS) {
                render(now, Math.min(0.05, pending));
                pending = 0;
            }
            frame = requestAnimationFrame(tick);
        }

        function onPointer(e) {
            const r = cvs.getBoundingClientRect();
            const x = ((e.clientX - r.left) / r.width) * 2 - 1;
            const y = 1 - ((e.clientY - r.top) / r.height) * 2;
            const now = performance.now();
            if (pointer.present && now > pointer.movedAt) {
                const speed = Math.hypot(x - pointer.x, y - pointer.y) / ((now - pointer.movedAt) / 1000);
                pointer.speed = Math.max(pointer.speed, speed);
            }
            if (!pointer.present || Math.hypot(x - pointer.ax, y - pointer.ay) > 0.03) {
                Object.assign(pointer, { ax: x, ay: y, movedAt: now });
            }
            Object.assign(pointer, { x, y, present: true });
        }
        function onLeave() { pointer.present = false; }
        function onVisibility() {
            cancelAnimationFrame(frame);
            last = 0;
            if (!document.hidden) frame = requestAnimationFrame(tick);
        }

        const observer = new ResizeObserver(resize);
        observer.observe(host);
        resize();
        if (!still) {
            cvs.addEventListener("pointermove", onPointer);
            cvs.addEventListener("pointerdown", onPointer);
            cvs.addEventListener("pointerleave", onLeave);
            document.addEventListener("visibilitychange", onVisibility);
            frame = requestAnimationFrame(tick);
        }
        return () => {
            stopped = true;
            observer.disconnect();
            cancelAnimationFrame(frame);
            cvs.removeEventListener("pointermove", onPointer);
            cvs.removeEventListener("pointerdown", onPointer);
            cvs.removeEventListener("pointerleave", onLeave);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    return <canvas ref={ref} className={`absolute left-0 top-0 touch-none ${className}`} />;
}

// One cell per ramp character, each in a bone tone that brightens with density.
function glyphAtlas(font, dpr) {
    const gw = CELL_W * dpr, gh = CELL_H * dpr;
    const atlas = document.createElement("canvas");
    atlas.width = gw * RAMP.length;
    atlas.height = gh;
    const g = atlas.getContext("2d");
    g.font = `${FONT_PX * dpr}px ${font}`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    [...RAMP].forEach((ch, i) => {
        const k = 0.35 + (0.65 * i) / (RAMP.length - 1);
        g.fillStyle = `rgb(${BONE.map((v) => Math.round(v * k)).join(",")})`;
        g.fillText(ch, i * gw + gw / 2, gh / 2);
    });
    return atlas;
}

function cellGrid(font, dpr, width, height, cols, rows) {
    const grid = document.createElement("canvas");
    grid.width = width;
    grid.height = height;
    const g = grid.getContext("2d");
    g.font = `${FONT_PX * dpr}px ${font}`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = `rgba(${BONE.join(",")},${GRID_ALPHA})`;
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) g.fillText(".", (c + 0.5) * CELL_W * dpr, (r + 0.5) * CELL_H * dpr);
    }
    return grid;
}
